import { normalizeCatalogOptions } from '@/utils/catalogOptions';
import { formatDateShortMonth } from '@/utils/datePresentation';

const resolveVoucherTypeCode = (row = {}) => String(
  row?.voucher_type
  || row?.voucherType
  || row?.voucher_type_code
  || ''
).trim().toLowerCase();

export const resolvePayPlanAmount = (row = {}) => {
  const hasInvoiceRelation = Number(row?.invoice_id || 0) > 0
    || String(row?.invoice || row?.invoice_reference || '').trim() !== ''
    || String(row?.invoice_date || '').trim() !== ''
    || Number(row?.invoiced || 0) === 1;

  const baseAmount = hasInvoiceRelation
    ? Number(row?.total || row?.subtotal || 0)
    : Number(row?.subtotal || row?.total || 0);

  return Number.isFinite(baseAmount) && baseAmount > 0 ? baseAmount : 0;
};

export const resolveExpenseFeePayAmount = (row = {}) => {
  const baseAmount = Number(row?.total || row?.amount || row?.subtotal || 0);
  return Number.isFinite(baseAmount) && baseAmount > 0 ? baseAmount : 0;
};

export const resolvePayPlanDate = (row = {}) => String(row?.date || '').trim();

export const resolvePayPlanCustomer = (row = {}, fallback = '') => (
  String(row?.customer_name || row?.company_name || '').trim() || fallback
);

export const resolvePayPlanVoucherTypeLabel = (row = {}, text = null) => {
  const voucherType = String(row?.voucher_type || '').trim().toLowerCase();
  const special = Number(row?.special || 0) === 1;
  const fallback = String(text?.payModal?.fallback?.emptyValue || '');

  if (voucherType === 'simple_note' || special) {
    return String(text?.payModal?.planCard?.voucherTypes?.simpleNote || fallback);
  }

  return String(text?.payModal?.planCard?.voucherTypes?.cfdi || fallback);
};

export const resolveAmountErrorMessage = (amount = 0, planAmount = 0, text = null) => {
  if (Number(amount || 0) <= Number(planAmount || 0)) {
    return '';
  }

  return String(text?.payModal?.validation?.amountGreaterThanPlan || '');
};

export const resolveAmountWarningMessage = (amount = 0, planAmount = 0, text = null) => {
  const normalizedAmount = Number(amount || 0);
  const normalizedPlan = Number(planAmount || 0);

  if (normalizedAmount <= 0 || normalizedAmount >= normalizedPlan) {
    return '';
  }

  return String(text?.payModal?.warnings?.splitPlan || '');
};

export const resolveRemainingAmount = (amount = 0, planAmount = 0) => Math.max(0, Number(planAmount || 0) - Number(amount || 0));

export const resolveDefaultTransferPaymentMethod = (options = []) => options.find((option) => {
  const label = String(option?.label || '').trim().toLowerCase();
  return label.includes('transferencia');
}) || null;

export const buildPaymentAccountOptions = (list = [], unassignedAccountLabel = '') => {
  const options = normalizeCatalogOptions(list);
  const label = String(unassignedAccountLabel || '');

  return [
    {
      value: '',
      label,
      meta: {
        id: 0,
        name: label,
        is_account_payment: false,
      },
    },
    ...options,
  ];
};

export const resolveDefaultPaymentAccount = (options = []) => (
  options.find((option) => Number(option?.meta?.is_account_payment || 0) === 1) || null
);

export const formatPayPlanDateShortMonth = (value, fallback = '') => (
  formatDateShortMonth(value, { fallback, locale: 'es-MX' })
);

export const resolvePayModalSimpleNoteMode = ({ context = '', row = {} } = {}) => {
  if (String(context || '').trim().toLowerCase() === 'expense-fee') {
    return true;
  }

  const voucherType = resolveVoucherTypeCode(row);
  const special = Number(row?.special || 0) === 1;

  if (special || voucherType === '') {
    return true;
  }

  return voucherType === 'simple_note';
};

export const resolveExpenseFeeCounterpartyLabel = (row = {}, text = null) => {
  const type = String(row?.commission_agent_type || '').trim().toLowerCase();
  const hasCollaborator = String(row?.collaborator || row?.commission_agent_name || '').trim() !== ''
    && type === 'collaborator';
  const fallback = String(text?.payModal?.fallback?.emptyValue || '');

  if (hasCollaborator) {
    return String(text?.payModal?.expenseFeeCard?.fields?.collaborator || fallback);
  }

  return String(text?.payModal?.expenseFeeCard?.fields?.supplier || fallback);
};

export const resolveExpenseFeeCounterpartyName = (row = {}, fallback = '') => {
  const type = String(row?.commission_agent_type || '').trim().toLowerCase();

  if (type === 'collaborator') {
    return String(row?.collaborator || row?.commission_agent_name || '').trim() || fallback;
  }

  return String(row?.provider || row?.commission_agent_name || '').trim() || fallback;
};

export const resolveExpenseFeeVoucherTypeLabel = (row = {}, text = null) => {
  const voucherType = resolveVoucherTypeCode(row);
  const voucherTypeLabel = String(
    row?.voucher_type_label
    || row?.voucherTypeLabel
    || row?.voucher_type_name
    || row?.tipo_de_comprobante
    || ''
  ).trim();
  const inferredVoucherType = voucherType === '' && Number(row?.special || 0) === 1
    ? 'simple_note'
    : voucherType;
  const fallback = String(text?.payModal?.fallback?.emptyValue || '');

  const map = {
    cfdi: String(text?.payModal?.voucherTypes?.cfdi || text?.payModal?.planCard?.voucherTypes?.cfdi || fallback),
    simple_note: String(text?.payModal?.voucherTypes?.simpleNote || text?.payModal?.planCard?.voucherTypes?.simpleNote || fallback),
    foreign_receipt: String(text?.payModal?.voucherTypes?.foreignReceipt || fallback),
    payroll: String(text?.payModal?.voucherTypes?.payroll || fallback),
    nomina: String(text?.payModal?.voucherTypes?.payroll || fallback),
  };

  if (map[inferredVoucherType]) {
    return map[inferredVoucherType];
  }

  if (voucherTypeLabel !== '') {
    return voucherTypeLabel;
  }

  if (voucherType !== '') {
    return voucherType.replace(/[_-]+/g, ' ');
  }

  return fallback;
};

export const resolveExpenseFeeEntityType = (entityType = '') => {
  const normalized = String(entityType || '').trim().toLowerCase();
  return normalized === 'fee' ? 'fee' : 'expense';
};

export const resolveExpenseFeeAmountErrorMessage = ({
  amount = 0,
  planAmount = 0,
  entityType = 'expense',
  text = null,
} = {}) => {
  if (Number(amount || 0) <= Number(planAmount || 0)) {
    return '';
  }

  if (String(entityType || '').trim().toLowerCase() === 'fee') {
    return String(text?.payModal?.validation?.amountGreaterThanFee || '');
  }

  return String(text?.payModal?.validation?.amountGreaterThanExpense || '');
};

export const resolveExpenseFeeAmountWarningMessage = ({
  amount = 0,
  planAmount = 0,
  entityType = 'expense',
  text = null,
} = {}) => {
  const normalizedAmount = Number(amount || 0);
  const normalizedPlan = Number(planAmount || 0);

  if (normalizedAmount <= 0 || normalizedAmount >= normalizedPlan) {
    return '';
  }

  if (String(entityType || '').trim().toLowerCase() === 'fee') {
    return String(text?.payModal?.warnings?.splitFee || '');
  }

  return String(text?.payModal?.warnings?.splitExpense || '');
};

export const resolvePayModalCustomerSearchLabel = (option = {}) => {
  const rfc = String(option?.meta?.rfc || option?.rfc || '').trim();
  const businessName = String(option?.meta?.businessName || option?.businessName || option?.label || '').trim();
  const clientName = String(option?.meta?.clientName || option?.clientName || '').trim();

  return [businessName, rfc, clientName].filter((item) => item !== '').join(' | ');
};

export const mapPayModalReceiverSelectorOption = (item = {}) => {
  const row = item && typeof item === 'object' ? item : {};

  return {
    value: Number(row?.contributor_id || row?.id || 0),
    label: String(row?.business_name || row?.name || row?.client_name || '').trim(),
    customerId: Number(row?.customer_id || row?.company_id || 0),
    meta: {
      id: Number(row?.contributor_id || row?.id || 0),
      customerId: Number(row?.customer_id || row?.company_id || 0),
      default: Number(row?.default || 0),
      clientName: String(row?.client_name || '').trim(),
      rfc: String(row?.rfc || '').trim(),
      businessName: String(row?.business_name || row?.name || '').trim(),
      cfdiUseCode: String(row?.cfdi_use || '').trim(),
      cfdiUse: String(row?.cfdi_use_label || row?.cfdi_use || '').trim(),
      taxRegimeCode: String(row?.tax_regime || '').trim(),
      taxRegime: String(row?.tax_regime_label || row?.tax_regime || '').trim(),
      fiscalResidence: String(row?.fiscal_residence || row?.zipcode || '').trim(),
      beneficiaryBankRfc: String(row?.rfc_banco_beneficiario || '').trim(),
      beneficiaryAccountNumber: String(row?.payment_account || '').trim(),
      orderingBank: String(row?.nom_banco_ord_ext || '').trim(),
      orderingBankRfc: String(row?.rfc_emisor_cta_ord || '').trim(),
      orderingBankAccount: String(row?.cta_ordenante || '').trim(),
    },
  };
};

export const mapPayModalContributorDetailOption = (payload = {}) => ({
  value: Number(payload?.id || 0),
  label: String(payload?.name || '').trim(),
  customerId: Number(payload?.company_id || 0),
  meta: {
    id: Number(payload?.id || 0),
    customerId: Number(payload?.company_id || 0),
    default: Number(payload?.default || 0),
    clientName: String(payload?.name || '').trim(),
    rfc: String(payload?.rfc || '').trim(),
    businessName: String(payload?.name || '').trim(),
    cfdiUseCode: String(payload?.uso_cfdi || '').trim(),
    cfdiUse: String(payload?.uso_cfdi_label || '').trim(),
    taxRegimeCode: String(payload?.regimen || '').trim(),
    taxRegime: String(payload?.regimen_label || '').trim(),
    fiscalResidence: String(payload?.zipcode || '').trim(),
    beneficiaryBankRfc: String(payload?.rfc_banco_beneficiario || '').trim(),
    beneficiaryAccountNumber: String(payload?.payment_account || '').trim(),
    orderingBank: String(payload?.nom_banco_ord_ext || '').trim(),
    orderingBankRfc: String(payload?.rfc_emisor_cta_ord || '').trim(),
    orderingBankAccount: String(payload?.cta_ordenante || '').trim(),
  },
});

export const extractPayModalBankData = (receiver = {}) => {
  const source = receiver?.meta || receiver || {};

  const data = {
    beneficiaryBankRfc: String(source?.beneficiaryBankRfc || source?.rfc_banco_beneficiario || '').trim(),
    beneficiaryAccountNumber: String(source?.beneficiaryAccountNumber || source?.paymentAccount || source?.payment_account || '').trim(),
    orderingBank: String(source?.orderingBank || source?.nom_banco_ord_ext || '').trim(),
    orderingBankRfc: String(source?.orderingBankRfc || source?.rfc_emisor_cta_ord || '').trim(),
    orderingBankAccount: String(source?.orderingBankAccount || source?.cta_ordenante || '').trim(),
  };

  return {
    ...data,
    expanded: Object.values(data).some((value) => String(value || '').trim() !== ''),
  };
};

export const resolveReceiverOptionByRfc = (receiverOptions = [], billedRfc = '') => {
  const normalizedBilledRfc = String(billedRfc || '').trim().toUpperCase();

  if (normalizedBilledRfc === '') {
    return null;
  }

  const matchesByRfc = receiverOptions.filter((option) => {
    const optionRfc = String(option?.meta?.rfc || option?.rfc || '').trim().toUpperCase();
    return optionRfc !== '' && optionRfc === normalizedBilledRfc;
  });

  if (matchesByRfc.length === 0) {
    return null;
  }

  return matchesByRfc.find((option) => Number(option?.meta?.default || option?.default || 0) === 1)
    || matchesByRfc[0]
    || null;
};

export const resolveReceiverOptionByContributorId = (receiverOptions = [], contributorId = 0) => {
  const normalizedContributorId = Number(contributorId || 0) || 0;

  if (normalizedContributorId <= 0) {
    return null;
  }

  return receiverOptions.find((option) => Number(option?.value || 0) === normalizedContributorId) || null;
};

export const resolveDefaultReceiverOption = (receiverOptions = []) => (
  receiverOptions.find((option) => Number(option?.meta?.default || option?.default || 0) === 1)
  || receiverOptions[0]
  || null
);
